import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  Bell,
  Users,
  BarChart3,
  Activity,
  Bot,
  Inbox,
  Server,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Wifi,
  WifiOff,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/ui/Avatar";
import { Tooltip } from "@/components/ui/Tooltip";
import { OrgSwitcher } from "@/components/layout/OrgSwitcher";
import { useAuthStore } from "@/stores/useAuthStore";
import { useDashboardWS } from "@/hooks/useDashboardWS";

/* ------------------------------------------------------------------ */
/*  Navigation items                                                   */
/* ------------------------------------------------------------------ */

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/dashboard/inbox", icon: Inbox, label: "Inbox" },
  { to: "/dashboard/agents/new", icon: Bot, label: "Agents" },
  { to: "/dashboard/channels", icon: Activity, label: "Channels" },
  { to: "/dashboard/sandbox", icon: Server, label: "Sandbox" },
  { to: "/dashboard/usage", icon: BarChart3, label: "Usage" },
  { to: "/dashboard/sessions", icon: MessageSquare, label: "Sessions" },
  { to: "/dashboard/team", icon: Users, label: "Team" },
  { to: "/dashboard/notifications", icon: Bell, label: "Notifications" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

/* ------------------------------------------------------------------ */
/*  Layout                                                             */
/* ------------------------------------------------------------------ */

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout, loadOrgs } = useAuthStore();
  const { isConnected } = useDashboardWS();

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  return (
    <div className="flex h-screen bg-surface text-white">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col border-r border-white/10 bg-surface transition-all duration-200 lg:static lg:z-auto",
          collapsed ? "w-16" : "w-60",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo area */}
        <div className="flex h-14 items-center justify-between border-b border-white/10 px-4">
          {!collapsed && (
            <span className="text-lg font-bold tracking-tight text-primary">
              AJNT
            </span>
          )}
          <button
            onClick={() => {
              setCollapsed((p) => !p);
              setMobileOpen(false);
            }}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-white/5 hover:text-white"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-white/5 hover:text-white",
                  collapsed && "justify-center px-2",
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom section */}
        <div className="border-t border-white/10 p-2">
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/5 hover:text-white",
              collapsed && "justify-center px-2",
            )}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 items-center justify-between border-b border-white/10 bg-surface px-4 lg:px-6">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen((p) => !p)}
            className="rounded-md p-1 text-muted-foreground lg:hidden"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* WebSocket connection status */}
            <Tooltip content={isConnected ? "Live connected" : "Disconnected"}>
              <div
                className="flex items-center gap-1.5 rounded-md px-2 py-1"
                data-testid="ws-status"
              >
                {isConnected ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <Wifi className="h-4 w-4 text-emerald-500" />
                  </>
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full bg-red-500" />
                    <WifiOff className="h-4 w-4 text-red-500" />
                  </>
                )}
              </div>
            </Tooltip>

            <OrgSwitcher />
            <button className="relative rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-white">
              <Bell className="h-5 w-5" />
              {/* Notification dot */}
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" />
            </button>
            <Avatar name={user?.name ?? "User"} size="sm" />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
